import * as Sentry from '@sentry/browser';

/**
 * Report a failed `POST /insurance/v2/eligibility` to Sentry.
 *
 * Every eligibility caller (insurance-dk `InsuranceContent`, the site-one
 * insurance store command, ace's insurance card) swallows this failure into a
 * silent redirect / `status: 'error'` / nothing. A 504 here is a lost
 * insurance sale that previously left no trace — same blind spot as MV-3309.
 *
 * Reported once, here, instead of per caller so new callers are covered by
 * default. `@sentry/browser` is a no-op when the host has not initialised a
 * client, so this is safe in every surface.
 *
 * Only IDs are forwarded — FirstName/LastName in the request are PII.
 *
 * Never throws — safe to call from any `onError`. A throw here would skip the
 * caller's recovery handler (react-query 3 runs `onError` before dispatching
 * the error state).
 */
export const reportEligibilityError = (
  error: unknown,
  variables: {
    PersonID?: string;
    CustomerID?: string;
    LoanApplicationID?: string;
    MarketCountry?: string;
    Source?: string;
  }
) => {
  try {
    const { PersonID, CustomerID, LoanApplicationID, MarketCountry, Source } =
      variables;

    Sentry.captureException(
      error instanceof Error ? error : new Error(String(error)),
      {
        level: 'error',
        tags: { flow: 'insurance', step: 'eligibility' },
        extra: {
          endpoint: 'POST /insurance/v2/eligibility',
          variables: {
            PersonID,
            CustomerID,
            LoanApplicationID,
            MarketCountry,
            Source,
          },
        },
      }
    );
  } catch {
    // Reporting must never throw into a caller's error path — see the
    // fetch-eligibility command, where a skipped onError pins eligibility
    // at `loading` for the session.
  }
};
